import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Layers, Bookmark, Heart, Info, LogIn, LogOut, Menu, X, Wrench, User, Trophy } from "lucide-react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { Avatar } from "./bits";
import { SUPPORT_ENABLED } from "../lib/features";

const navItems = [
  { to: "/deck", label: "Swipe", Icon: Layers },
  { to: "/explore", label: "Browse all", Icon: Layers },
  { to: "/saved", label: "Saved", Icon: Bookmark },
  ...(SUPPORT_ENABLED ? [{ to: "/supported", label: "My support", Icon: Heart }] : []),
  { to: "/about", label: "About", Icon: Info },
];

function Header() {
  const { auth, signOut, saved } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleSignOut = async () => { await signOut(); toast("Signed out. See you soon!"); setOpen(false); };

  const roleLink = auth.role === "builder"
    ? { to: "/builder", label: "My project", Icon: Wrench }
    : auth.is_admin ? { to: "/admin", label: "Admin", Icon: Wrench } : null;

  return (
    <header className="sticky top-0 z-40 h-14 w-full glass border-b border-white/10" data-testid="header">
      <div className="mx-auto flex h-full max-w-6xl items-center justify-between px-4">
        <Link to="/deck" className="flex items-center gap-2" data-testid="brand">
          <img src="/assets/ucenter-circle.png" alt="" className="h-8 w-8 rounded-full" />
          <div className="leading-none">
            <span className="block font-display text-[15px] font-extrabold text-white">U-Hack Showcase</span>
            <span className="block text-[10px] text-gold-400">Grow together. People first.</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/winners" data-testid="nav-winners-link"
            className={({ isActive }) => `flex items-center gap-1 rounded-pill px-3 py-1.5 text-[14px] font-bold transition-colors ${isActive ? "bg-gold-500 text-navy-900" : "text-gold-400 hover:bg-gold-500/15"}`}>
            <Trophy size={14} /> Winners
          </NavLink>
          {navItems.map(({ to, label }) => (
            <NavLink key={to} to={to} data-testid={`nav-${label.replace(/\s/g, "-").toLowerCase()}`}
              className={({ isActive }) => `rounded-pill px-3 py-1.5 text-[14px] font-semibold transition-colors ${isActive ? "bg-white/10 text-gold-400" : "text-on-dark-muted hover:text-white"}`}>
              {label}{label === "Saved" && saved.length ? ` (${saved.length})` : ""}
            </NavLink>
          ))}
          {roleLink && (
            <NavLink to={roleLink.to} data-testid="nav-role-link"
              className={({ isActive }) => `flex items-center gap-1 rounded-pill px-3 py-1.5 text-[14px] font-semibold transition-colors ${isActive ? "bg-white/10 text-gold-400" : "text-on-dark-muted hover:text-white"}`}>
              <roleLink.Icon size={14} /> {roleLink.label}
            </NavLink>
          )}
          {auth.authenticated ? (
            <>
              <NavLink to="/profile" data-testid="nav-profile"
                className={({ isActive }) => `ml-1 flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[14px] font-semibold transition-colors ${isActive ? "bg-white/10 text-gold-400" : "text-on-dark-muted hover:text-white"}`}>
                <Avatar name={auth.display_name || auth.email} src={auth.photo_url} size={24} /> Profile
              </NavLink>
              <button onClick={handleSignOut} data-testid="nav-signout" className="ml-1 flex items-center gap-1 rounded-pill bg-white/10 px-3 py-1.5 text-[14px] font-semibold text-white">
                <LogOut size={15} /> Sign out
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/signin")} data-testid="nav-signin" className="ml-2 flex items-center gap-1 rounded-pill bg-gold-500 px-3 py-1.5 text-[14px] font-bold text-navy-900">
              <LogIn size={15} /> Sign in
            </button>
          )}
        </nav>

        <button className="md:hidden" onClick={() => setOpen((o) => !o)} aria-label="Menu" data-testid="menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-b border-white/10 bg-navy-950/95 px-4 py-3 backdrop-blur-xl md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            <NavLink to="/winners" onClick={() => setOpen(false)} data-testid="mobile-nav-winners-link"
              className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-bold ${isActive ? "bg-gold-500 text-navy-900" : "bg-gold-500/10 text-gold-400"}`}>
              <Trophy size={16} /> Winners
            </NavLink>
            {navItems.map(({ to, label, Icon }) => (
              <NavLink key={to} to={to} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-semibold ${isActive ? "bg-white/10 text-gold-400" : "text-white"}`}>
                <Icon size={16} /> {label}{label === "Saved" && saved.length ? ` (${saved.length})` : ""}
              </NavLink>
            ))}
            {roleLink && (
              <NavLink to={roleLink.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-semibold ${isActive ? "bg-white/10 text-gold-400" : "text-white"}`}>
                <roleLink.Icon size={16} /> {roleLink.label}
              </NavLink>
            )}
            {auth.authenticated ? (
              <>
                <NavLink to="/profile" onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-semibold ${isActive ? "bg-white/10 text-gold-400" : "text-white"}`}>
                  <User size={16} /> Profile
                </NavLink>
                <button onClick={handleSignOut} className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-[15px] font-semibold text-white"><LogOut size={16} /> Sign out</button>
              </>
            ) : (
              <button onClick={() => { setOpen(false); navigate("/signin"); }} className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-[15px] font-semibold text-gold-400"><LogIn size={16} /> Sign in</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-auto border-t border-white/10 px-4 py-6 text-center text-[13px] text-on-dark-muted" data-testid="footer">
      <div className="flex items-center justify-center gap-4">
        <Link to="/privacy" className="hover:text-white">Privacy</Link>
        <Link to="/terms" className="hover:text-white">Terms</Link>
        <Link to="/about" className="hover:text-white">About</Link>
      </div>
      <p className="mx-auto mt-3 max-w-xl text-[11px] text-ink-400">
        Projects are independent hackathon entries by their builders. They aren't official U-Topia products, and some features may be prototypes or simulations.
      </p>
    </footer>
  );
}

export default function Shell({ children, footer = true }) {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col">{children}</main>
      {footer && <Footer />}
    </div>
  );
}
